'use client';

import type * as braze from '@braze/web-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getUiConfig } from '@/config/client/getUiConfig';
import { BRAZE_EVENTS } from '@/features/analytics/braze/constants/brazeConstants';

type BrazeUniqueId = string | null;
type BrazeEventKeys = keyof typeof BRAZE_EVENTS;
type BrazeEvent = (typeof BRAZE_EVENTS)[BrazeEventKeys];

interface BrazeConfig {
  brazeApiKey: string;
  brazeEndpoint: string;
  isBrazeLoggingEnabled: boolean;
}

export interface BrazeFunctions {
  braze: typeof braze | null;
  changeUser: (uniqueId?: BrazeUniqueId) => void;
  setBrazeUserLoggedIn: () => void;
  logCustomEvent: (contentPageType: string, event?: BrazeEvent) => void;
}

export function useBraze(config: BrazeConfig, uniqueId: BrazeUniqueId = null): BrazeFunctions {
  const [brazeInstance, setBrazeInstance] = useState<typeof braze | null>(null);
  const loadedBrazeFirstTime = useRef(false);

  const setBrazeUserLoggedIn = useCallback(() => {
    if (!brazeInstance) {
      return;
    }

    const uiConfig = getUiConfig();

    if (uiConfig.isBrazeCustomLoggingEnabled) {
      brazeInstance.logCustomEvent(BRAZE_EVENTS.LOGGED_IN, {
        platform_description: 'web',
        date: new Date(),
      });
    }
  }, [brazeInstance]);

  const logCustomEvent = useCallback(
    (contentPageType: string, event?: BrazeEvent) => {
      const uiConfig = getUiConfig();

      if (brazeInstance && uiConfig.isBrazeCustomLoggingEnabled) {
        brazeInstance.logCustomEvent(event ?? BRAZE_EVENTS.VIEW, {
          os: 'web',
          date: new Date(),
          content_pagetype: contentPageType,
        });
      }
    },
    [brazeInstance],
  );

  const changeUser = (userUniqueId: BrazeUniqueId = null): void => {
    if (!brazeInstance) {
      return;
    }

    if (userUniqueId) {
      brazeInstance.changeUser(userUniqueId);
    }

    if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev') {
      brazeInstance.requestImmediateDataFlush();
    }
  };

  useEffect(() => {
    const uiConfig = getUiConfig();

    if (!uiConfig.isBrazeEnabled) {
      return;
    }

    void (async () => {
      const braze = await import('@braze/web-sdk');

      braze.initialize(config.brazeApiKey, {
        baseUrl: config.brazeEndpoint,
        enableLogging: config.isBrazeLoggingEnabled,
        allowUserSuppliedJavascript: true,
      });
      setBrazeInstance(braze);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run this effect once
  }, []);

  useEffect(() => {
    if (brazeInstance && !loadedBrazeFirstTime.current) {
      loadedBrazeFirstTime.current = true;

      changeUser(uniqueId);
      brazeInstance.automaticallyShowInAppMessages();
      brazeInstance.openSession();
      brazeInstance.logCustomEvent(BRAZE_EVENTS.APP_LAUNCH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run this effect if braze changes
  }, [brazeInstance]);

  return {
    braze: brazeInstance,
    changeUser,
    setBrazeUserLoggedIn,
    logCustomEvent,
  };
}
